// src/api/dataHandler.js
import dayjs from "dayjs";

// ✅ NEW dataset
const BASE_URL =
  "https://api.energidataservice.dk/dataset/ConsumptionConsumerCategoryHour";

// ✅ We always query this region
const REGION_NAME = "Region Nordjylland";

// Simple in-memory cache
const dataCache = new Map();

/**
 * Builds a stable cache key (region is fixed, so only dates matter).
 */
const buildCacheKey = ({ startDate, endDate }) =>
  `${REGION_NAME}_${startDate}_${endDate}`;

/**
 * Builds the full API URL for given parameters.
 * Note: municipalityNo is ignored now (kept in the signature for backwards compatibility).
 */
export const buildApiUrl = ({ startDate, endDate }) => {
  // ✅ NEW filter keys for the new dataset
  const filter = {
    RegionName: [REGION_NAME],
    ConsumerCategory2: ["Privat"],
  };

  const query = new URLSearchParams({
    offset: 0,
    start: `${startDate}`,
    end: `${endDate}`,
    filter: JSON.stringify(filter),
    // ✅ New time column name for sorting
    sort: "TimeUTC DESC",
  });

  return `${BASE_URL}?${query.toString()}`;
};

/**
 * Fetches consumption data from the API.
 * municipalityNo kept for compatibility but no longer used.
 */
export const fetchConsumptionData = async ({
  municipalityNo,
  startDate,
  endDate,
}) => {
  const cacheKey = buildCacheKey({ startDate, endDate });
  if (dataCache.has(cacheKey)) return dataCache.get(cacheKey);

  const url = buildApiUrl({ startDate, endDate });

  try {
    const response = await fetch(url);
    const json = await response.json();
    const data = json.records || [];
    dataCache.set(cacheKey, data);
    return data;
  } catch (error) {
    console.error("API fetch error:", error);
    return [];
  }
};

/**
 * Transforms raw API data into average consumption per hour (0–23).
 * ✅ Uses TimeDK and ConsumptionkWh from the new dataset.
 */
export const calculateAveragePerHour = (data) => {
  const hourlySums = Array(24).fill(0);
  const hourlyCounts = Array(24).fill(0);

  data.forEach((entry) => {
    const hour = new Date(entry.TimeDK).getHours(); // was HourDK
    const kWh = parseFloat(entry.ConsumptionkWh);
    if (!isNaN(kWh)) {
      hourlySums[hour] += kWh;
      hourlyCounts[hour] += 1;
    }
  });

  return hourlySums.map((sum, hour) =>
    hourlyCounts[hour] ? sum / hourlyCounts[hour] : 0
  );
};

/**
 * Calculates the proportion of total consumption for each hour.
 * Returns an array of 24 values between 0 and 1.
 * ✅ Uses TimeDK and ConsumptionkWh.
 */
export const calculateProportionPerHour = (data) => {
  const dayMap = new Map(); // key: date (YYYY-MM-DD), value: array[24] of kWh

  data.forEach((entry) => {
    const dt = new Date(entry.TimeDK); // was HourDK
    const dateKey = dt.toISOString().split("T")[0];
    const hour = dt.getHours();
    const kWh = parseFloat(entry.ConsumptionkWh);

    if (!isNaN(kWh)) {
      if (!dayMap.has(dateKey)) {
        dayMap.set(dateKey, Array(24).fill(0));
      }
      dayMap.get(dateKey)[hour] += kWh;
    }
  });

  // Now calculate normalized hourly proportion per day
  const proportionSums = Array(24).fill(0);
  let dayCount = 0;

  dayMap.forEach((hourlyValues) => {
    const dayTotal = hourlyValues.reduce((a, b) => a + b, 0);
    if (dayTotal > 0) {
      hourlyValues.forEach((kWh, hour) => {
        proportionSums[hour] += kWh / dayTotal;
      });
      dayCount += 1;
    }
  });

  // Average proportions across all days
  return proportionSums.map((sum) => (dayCount > 0 ? sum / dayCount : 0));
};

/**
 * Compares proportionPerHour between two datasets and returns percentage changes.
 */
export const compareProportionPerHour = (data1, data2) => {
  const proportions1 = calculateProportionPerHour(data1);
  const proportions2 = calculateProportionPerHour(data2);

  return proportions1.map((p1, hour) => {
    const p2 = proportions2[hour];
    if (p1 === 0 && p2 === 0) return 0;
    if (p1 === 0) return Infinity; // Or null/NaN depending on your UI
    return ((p2 - p1) / p1) * 100;
  });
};

const calculateHourlyStats = (data) => {
  const hourlySums = Array(24).fill(0);
  const hourlyCounts = Array(24).fill(0);
  let totalConsumption = 0;

  data.forEach((entry) => {
    const hour = new Date(entry.TimeDK).getHours(); // was HourDK
    const kWh = parseFloat(entry.ConsumptionkWh);
    if (!isNaN(kWh)) {
      hourlySums[hour] += kWh;
      hourlyCounts[hour] += 1;
      totalConsumption += kWh;
    }
  });

  const averagePerHour = hourlySums.map((sum, hour) =>
    hourlyCounts[hour] ? sum / hourlyCounts[hour] : 0
  );
  const proportionPerHour = hourlySums.map((sum) =>
    totalConsumption > 0 ? sum / totalConsumption : 0
  );

  return { averagePerHour, proportionPerHour };
};

/**
 * Compares two datasets:
 * - Difference in average kWh per hour
 * - Percent change in proportion per hour
 */
export const compareHourlyLoad = (data1, data2) => {
  const stats1 = calculateHourlyStats(data1);
  const stats2 = calculateHourlyStats(data2);

  const averageDiff = stats2.averagePerHour.map(
    (avg2, hour) => avg2 - stats1.averagePerHour[hour]
  );

  const proportionChangePercent = stats2.proportionPerHour.map((p2, hour) => {
    const p1 = stats1.proportionPerHour[hour];
    if (p1 === 0 && p2 === 0) return 0;
    if (p1 === 0) return Infinity;
    return ((p2 - p1) / p1) * 100;
  });

  return {
    averageDiffPerHour: averageDiff,
    proportionChangePerHourPercent: proportionChangePercent,
  };
};

/**
 * Maps hour to last category.
 */
const getLastCategory = (hour) => {
  if ([0, 1, 2, 3, 4, 5].includes(hour)) return "lav";
  if ([6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 21, 22, 23].includes(hour))
    return "høj";
  if ([17, 18, 19, 20].includes(hour)) return "spids";
  return "ukendt";
};

/**
 * Calculates average hourly consumption and proportion per last category, per month.
 * ✅ Uses TimeDK and ConsumptionkWh.
 */
export const calculateMonthlyLoadProfile = (data) => {
  const groupMap = new Map();
  const categoryHourCounts = {};
  const totalMonthlyConsumption = {};
  const daysInMonth = (year, month) => new Date(year, month, 0).getDate(); // month = 1-based
  const actualDaysInMonth = {};

  data.forEach((entry) => {
    const dt = new Date(entry.TimeDK); // was HourDK
    const dateKey = dt.toISOString().split("T")[0]; // (unused)
    const year = dt.getFullYear();
    const month = dt.getMonth() + 1;
    const hour = dt.getHours();
    const last = getLastCategory(hour);
    const kWh = parseFloat(entry.ConsumptionkWh);
    if (isNaN(kWh)) return;

    // ✅ Count actual unique days with data
    const day = dt.getDate();
    const actualKey = `${year}-${month}`;
    if (!actualDaysInMonth[actualKey]) {
      actualDaysInMonth[actualKey] = new Set();
    }
    actualDaysInMonth[actualKey].add(day);

    const key = `${year}-${month}-${last}`;
    const monthKey = `${year}-${month}`;

    // Track hours per category per month
    if (!categoryHourCounts[monthKey]) {
      categoryHourCounts[monthKey] = { lav: 0, høj: 0, spids: 0 };
    }
    categoryHourCounts[monthKey][last]++;

    // Initialize group
    if (!groupMap.has(key)) {
      groupMap.set(key, { year, month, last, totalKWh: 0, count: 0 });
    }
    const group = groupMap.get(key);
    group.totalKWh += kWh;
    group.count += 1;

    // Track total consumption per month
    if (!totalMonthlyConsumption[monthKey]) {
      totalMonthlyConsumption[monthKey] = 0;
    }
    totalMonthlyConsumption[monthKey] += kWh;
  });

  const seen = new Set();
  let duplicateCount = 0;
  data.forEach((d) => {
    const key = d.TimeDK; // was HourDK
    if (seen.has(key)) duplicateCount++;
    seen.add(key);
  });

  const results = Array.from(groupMap.values()).map(
    ({ year, month, last, totalKWh, count }) => {
      const monthKey = `${year}-${month}`;
      const totalMonthKWh = totalMonthlyConsumption[monthKey] || 0;
      if (year === 2025 && (month === 5 || month === 6)) {
        const dim = actualDaysInMonth[`${year}-${month}`]?.size || 1;
        const propPerHour =
          totalMonthKWh > 0 ? totalKWh / totalMonthKWh / (count / dim) : 0;
      }
      return {
        year,
        month,
        last,
        averageHourlyTotalConsumption: totalKWh / count,
        averageHourlyProportionConsumption:
          totalMonthKWh > 0
            ? totalKWh /
              totalMonthKWh /
              (count / (actualDaysInMonth[`${year}-${month}`]?.size || 1))
            : 0,
      };
    }
  );

  const proportionsPerMonth = {};
  results.forEach(
    ({ year, month, last, averageHourlyProportionConsumption }) => {
      const key = `${year}-${String(month).padStart(2, "0")}`;
      if (!proportionsPerMonth[key]) {
        proportionsPerMonth[key] = { lav: 0, høj: 0, spids: 0 };
      }
      proportionsPerMonth[key][last] = averageHourlyProportionConsumption;
    }
  );

  // Optional debugging (kept from your original)
  Object.entries(proportionsPerMonth).forEach(([monthKey, values]) => {
    const scaledLav = (values.lav ?? 0) * 6;
    const scaledHoj = (values.høj ?? 0) * 14;
    const scaledSpids = (values.spids ?? 0) * 4;
    const sum = scaledLav + scaledHoj + scaledSpids;
  });

  const totalHoursPerMonth = {};
  Array.from(groupMap.values()).forEach(({ year, month, count }) => {
    const key = `${year}-${String(month).padStart(2, "0")}`;
    if (!totalHoursPerMonth[key]) totalHoursPerMonth[key] = 0;
    totalHoursPerMonth[key] += count;
  });

  return results;
};

export const getLatestYearMonth = (data) => {
  let latestDate = null;
  for (const entry of data) {
    const dt = new Date(entry.TimeDK); // was HourDK
    if (!latestDate || dt > latestDate) {
      latestDate = dt;
    }
  }
  return {
    year: latestDate.getFullYear(),
    month: latestDate.getMonth() + 1, // month is 0-indexed
  };
};
